import {ChangeEventHandler, FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import styled, {useTheme} from 'styled-components';

import {asNumber} from '@shared/lib/utils/type_utils';

import {UnthemedButton} from '@shared_web/components/core/button';
import {Input} from '@shared_web/components/core/input';
import {SvgIcon, SvgIconData} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {chevronLeftIcon} from '@shared_web/components/old_icons/chevron_left_icon';
import {chevronRightIcon} from '@shared_web/components/old_icons/chevron_right_icon';
import {optionalPx} from '@shared_web/lib/styled_utils';

export interface PaginationUIProps {
  // How the UI should behave when there is only one page
  // hide: nothing is rendered
  // light: only the total number of items is shown
  // full: the full navigation UI is shown
  singlePageBehavior?: 'hide' | 'light' | 'full';
  itemsCountLabel?: (itemCount: number, locale: string | undefined) => ReactNode;
  itemsPerPageLabel?: (limit: number, locale: string | undefined) => ReactNode;
  locale: string | undefined;
}

interface PaginationProps extends PaginationUIProps {
  totalItems: number;
  limit?: number;
  onPageChange?: (page: number) => void;
}

const defaultItemsCountLabel = (itemCount: number) => `${itemCount} items`;
const defaultItemsPerPageLabel = (limit: number) => ` (${limit} per page)`;

export const Pagination: FC<PaginationProps> = props => {
  const {
    totalItems,
    limit,
    onPageChange,
    singlePageBehavior = 'light',
    itemsCountLabel = defaultItemsCountLabel,
    itemsPerPageLabel = defaultItemsPerPageLabel,
    locale,
  } = props;
  const totalPages = limit && totalItems > 0 ? Math.ceil(totalItems / limit) : 1;

  const theme = useTheme();
  const textColor = theme.main?.textColor;
  const fontSize = theme.main?.fontSize;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage, onPageChange]);

  // Ensure the current page is not out of bounds (e.g. when the number of items changes)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Prev and next page buttons
  const updateCurrentPageAndInput = useCallback((page: number) => {
    setCurrentPage(page);
    setCurrentPageInput(String(page));
  }, []);
  const handlePrevPage = useCallback(
    () => updateCurrentPageAndInput(Math.max(1, currentPage - 1)),
    [currentPage, updateCurrentPageAndInput]
  );
  const handleNextPage = useCallback(
    () => updateCurrentPageAndInput(Math.min(totalPages, currentPage + 1)),
    [currentPage, totalPages, updateCurrentPageAndInput]
  );

  //
  // Input element
  //
  const [currentPageInput, setCurrentPageInput] = useState(String(currentPage));

  const handlePageInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    evt => {
      const val = evt.target.value;
      setCurrentPageInput(val);
      const num = asNumber(val);
      if (num === undefined) {
        return;
      }
      setCurrentPage(Math.max(1, Math.min(totalPages, num)));
    },
    [totalPages]
  );

  const handlePageInputBlur = useCallback(
    () => setCurrentPageInput(String(currentPage)),
    [currentPage]
  );

  const navButton = useCallback(
    (opts: {onClick: () => void; disabled: boolean; icon: SvgIconData}) => {
      const {onClick, disabled, icon} = opts;
      return (
        <PaginationNavButton onClick={onClick} disabled={disabled}>
          <SvgIcon icon={icon} size={11} color={disabled ? `${textColor}80` : textColor} />
        </PaginationNavButton>
      );
    },
    [textColor]
  );
  const inputWidth = useMemo(() => {
    const maxNumDigits = String(totalPages).length;
    const inputWidth = 13 + 8.8 * maxNumDigits; // Approximative value that work well at font size 14px
    return inputWidth;
  }, [totalPages]);

  const itemsCountLabelStr = useMemo(
    () => itemsCountLabel(totalItems, locale),
    [itemsCountLabel, totalItems, locale]
  );
  const itemsPerPageLabelStr = useMemo(
    () => (limit === undefined ? '' : itemsPerPageLabel(limit, locale)),
    [itemsPerPageLabel, limit, locale]
  );

  // If there is no pagination, don't show the pagination UI
  const hasPagination = limit !== undefined && totalItems > limit;
  if (!hasPagination && singlePageBehavior === 'hide') {
    return <></>;
  }

  return (
    <Wrapper $gap="m" $align="center" $justify="space-between" $width="100%">
      {(hasPagination || singlePageBehavior !== 'hide') && (
        <PaginationInfo $gap="s">
          {itemsCountLabelStr}
          {hasPagination && itemsPerPageLabelStr}
        </PaginationInfo>
      )}
      {(hasPagination || singlePageBehavior === 'full') && (
        <Flex $gap="s" $align="center">
          {navButton({onClick: handlePrevPage, disabled: currentPage === 1, icon: chevronLeftIcon})}
          <PaginationInput
            value={currentPageInput}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
            width={inputWidth}
            $fontSize={fontSize}
          />
          <div> / {totalPages}</div>
          {navButton({
            onClick: handleNextPage,
            disabled: currentPage === totalPages,
            icon: chevronRightIcon,
          })}
        </Flex>
      )}
    </Wrapper>
  );
};

Pagination.displayName = 'Pagination';

const Wrapper = styled(Flex)`
  padding: 0 8px;
`;

const PaginationNavButton = styled(UnthemedButton)`
  padding: 8px;
  background-color: transparent;
  border-radius: 4px;
  &:hover {
    background-color: ${p => p.theme.main?.highlight1};
  }
`;

const PaginationInput = styled(Input)<{
  $fontSize?: string | number;
}>`
  height: auto;
  padding: 0;
  text-align: center;
  ${p => optionalPx('font-size', p.$fontSize)}
`;

const PaginationInfo = styled(Flex)`
  font-size: 14px;
  font-style: italic;
  color: ${p => p.theme.main?.highlight8};
`;
